import { useRef, useEffect, useState, useCallback } from 'react';

// Earth texture — NASA Blue Marble (equirectangular).
// Wikimedia's /thumb/ endpoint started returning 400 in 2026 for unauthenticated
// hot-link traffic, so we mirror through jsDelivr (GitHub-backed CDN) which serves
// the same image with CORS headers.
const EARTH_TEXTURE_URL =
  'https://cdn.jsdelivr.net/gh/turban/webgl-earth@master/images/2_no_clouds_4k.jpg';

// India is at ~78°E → (78+180)/360 = 0.7167 fraction of image width
// Setting initial rotation so India faces forward on first render
const INDIA_FRAC = 0.7167;

export default function useSpaceLoader({ loadingComplete, onComplete, minDisplayMs = 800 }) {
  const canvasRef    = useRef(null);
  const rafRef       = useRef(null);
  const startTimeRef = useRef(Date.now());
  const calledRef    = useRef(false);
  const frameRef     = useRef(0);

  // Earth texture image
  const earthImgRef  = useRef(null);
  // Globe rotation (radians) — starts so India is centered
  const rotRef       = useRef(0);

  // Pre-computed static data
  const starsRef  = useRef(null);
  const issRef    = useRef({ angle: -Math.PI / 2 });

  const satellitesRef = useRef([]);
  const debrisRef     = useRef([]);

  const [score,        setScore]        = useState(0);
  const [loadProgress, setLoadProgress] = useState(0);
  const loadProgressRef    = useRef(0);
  const loadingCompleteRef = useRef(false);

  // Keep refs in sync with props/state
  useEffect(() => { loadingCompleteRef.current = loadingComplete; }, [loadingComplete]);
  useEffect(() => { loadProgressRef.current = loadProgress; },     [loadProgress]);

  // ── Load Earth texture ────────────────────────────────────────────────────────
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => { earthImgRef.current = img; };
    img.onerror = () => { /* keep null → fallback drawn globe */ };
    img.src = EARTH_TEXTURE_URL;
  }, []);

  // ── Init static star data ─────────────────────────────────────────────────────
  useEffect(() => {
    starsRef.current = Array.from({ length: 260 }, () => ({
      xf:         Math.random(),
      yf:         Math.random(),
      r:          0.3 + Math.random() * 1.4,
      phase:      Math.random() * Math.PI * 2,
      brightness: 0.4 + Math.random() * 0.6,
    }));
  }, []);

  // ── Progress simulation ───────────────────────────────────────────────────────
  useEffect(() => {
    let raf;
    function tick() {
      setLoadProgress(prev => {
        if (loadingCompleteRef.current) return Math.min(100, prev + 4);
        if (prev >= 85) return prev;
        return Math.min(85, prev + 0.08 + Math.random() * 0.15);
      });
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ── Launch satellite on click ─────────────────────────────────────────────────
  const launchSatellite = useCallback((canvas) => {
    const cx     = canvas.width / 2;
    const cy     = canvas.height / 2;
    const earthR = 0.20 * Math.min(canvas.width, canvas.height);
    const orbitR = earthR * (1.3 + Math.random() * 1.5);
    const speed  = (0.005 + Math.random() * 0.009) * (Math.random() < 0.5 ? 1 : -1);
    satellitesRef.current.push({ orbitR, speed, angle: -Math.PI / 2, trail: [] });

    for (let i = 0; i < 10; i++) {
      debrisRef.current.push({
        x:  cx,
        y:  cy - earthR,
        vx: (Math.random() - 0.5) * 3.5,
        vy: -1.5 - Math.random() * 2.5,
        life: 1.0,
      });
    }
    setScore(s => s + 10);
  }, []);

  // ── Main draw loop ────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !starsRef.current) return;
    const ctx = canvas.getContext('2d');

    function resize() {
      canvas.width  = canvas.offsetWidth  || window.innerWidth;
      canvas.height = canvas.offsetHeight || window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    function draw() {
      frameRef.current++;
      const W      = canvas.width;
      const H      = canvas.height;
      const cx     = W / 2;
      const cy     = H / 2;
      const minDim = Math.min(W, H);
      const earthR = 0.20 * minDim;

      ctx.clearRect(0, 0, W, H);

      // ── Deep space background ──────────────────────────────────────────────
      ctx.fillStyle = '#020a18';
      ctx.fillRect(0, 0, W, H);

      // ── Milky Way subtle band ──────────────────────────────────────────────
      const mwGrad = ctx.createLinearGradient(0, H * 0.1, W, H * 0.9);
      mwGrad.addColorStop(0,   'rgba(60,80,120,0)');
      mwGrad.addColorStop(0.4, 'rgba(60,80,120,0.07)');
      mwGrad.addColorStop(0.6, 'rgba(80,60,120,0.06)');
      mwGrad.addColorStop(1,   'rgba(60,80,120,0)');
      ctx.fillStyle = mwGrad;
      ctx.fillRect(0, 0, W, H);

      // ── Stars ──────────────────────────────────────────────────────────────
      const t = frameRef.current;
      const stars = starsRef.current;
      for (let i = 0; i < stars.length; i++) {
        const s   = stars[i];
        const bri = (0.3 + 0.7 * Math.sin(t * 0.025 + s.phase)) * s.brightness;
        ctx.beginPath();
        ctx.arc(s.xf * W, s.yf * H, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,220,255,${bri.toFixed(2)})`;
        ctx.fill();
      }

      // ── Moon ──────────────────────────────────────────────────────────────
      const moonX = W * 0.80;
      const moonY = H * 0.15;
      const moonR = minDim * 0.038;
      const moonGrad = ctx.createRadialGradient(
        moonX - moonR * 0.35, moonY - moonR * 0.35, moonR * 0.1,
        moonX, moonY, moonR
      );
      moonGrad.addColorStop(0, '#d0d8e0');
      moonGrad.addColorStop(1, '#60707a');
      ctx.beginPath();
      ctx.arc(moonX, moonY, moonR, 0, Math.PI * 2);
      ctx.fillStyle = moonGrad;
      ctx.fill();

      // ── Earth ─────────────────────────────────────────────────────────────
      // Advance rotation (slow eastward drift)
      rotRef.current += 0.0008;

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, earthR, 0, Math.PI * 2);
      ctx.clip();

      const img = earthImgRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        // ── Textured spinning globe (cylindrical projection) ──────────────
        const drawH = earthR * 2;
        const drawW = (img.naturalWidth / img.naturalHeight) * drawH;

        // India (71.67% of image) is centered at globe start
        const rotFrac = (rotRef.current / (2 * Math.PI)) % 1;
        const centerFrac = ((INDIA_FRAC + rotFrac) % 1 + 1) % 1;
        const startX = cx - centerFrac * drawW;

        // Draw 3 copies for seamless horizontal wrap
        ctx.drawImage(img, startX - drawW, cy - earthR, drawW, drawH);
        ctx.drawImage(img, startX,         cy - earthR, drawW, drawH);
        ctx.drawImage(img, startX + drawW, cy - earthR, drawW, drawH);
      } else {
        // ── Fallback: programmatic globe if image not yet loaded ──────────
        const eg = ctx.createRadialGradient(
          cx - earthR * 0.3, cy - earthR * 0.3, earthR * 0.05,
          cx, cy, earthR
        );
        eg.addColorStop(0,   '#4a9fd5');
        eg.addColorStop(0.5, '#2a6fa8');
        eg.addColorStop(1,   '#0d2a5c');
        ctx.fillStyle = eg;
        ctx.beginPath();
        ctx.arc(cx, cy, earthR, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      // ── Sphere shading overlay (makes flat texture look like a sphere) ──
      const shadeGrad = ctx.createRadialGradient(
        cx - earthR * 0.28, cy - earthR * 0.35, earthR * 0.02,
        cx, cy, earthR
      );
      shadeGrad.addColorStop(0,    'rgba(255,255,255,0.10)');
      shadeGrad.addColorStop(0.35, 'rgba(0,0,0,0)');
      shadeGrad.addColorStop(0.75, 'rgba(0,0,0,0.25)');
      shadeGrad.addColorStop(1,    'rgba(0,0,0,0.65)');
      ctx.beginPath();
      ctx.arc(cx, cy, earthR, 0, Math.PI * 2);
      ctx.fillStyle = shadeGrad;
      ctx.fill();

      // ── Atmosphere glow ───────────────────────────────────────────────────
      const atmGrad = ctx.createRadialGradient(cx, cy, earthR * 0.95, cx, cy, earthR * 1.18);
      atmGrad.addColorStop(0,   'rgba(100,180,255,0.22)');
      atmGrad.addColorStop(0.5, 'rgba(60,120,220,0.08)');
      atmGrad.addColorStop(1,   'rgba(40,80,180,0)');
      ctx.beginPath();
      ctx.arc(cx, cy, earthR * 1.18, 0, Math.PI * 2);
      ctx.fillStyle = atmGrad;
      ctx.fill();

      // ── Earth outline (thin rim light) ───────────────────────────────────
      ctx.beginPath();
      ctx.arc(cx, cy, earthR, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(100,180,255,0.18)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // ── ISS orbit path (dashed) ───────────────────────────────────────────
      const issOrbitR = earthR * 1.42;
      ctx.save();
      ctx.setLineDash([4, 8]);
      ctx.beginPath();
      ctx.arc(cx, cy, issOrbitR, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(120,180,255,0.15)';
      ctx.lineWidth   = 0.6;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();

      // ── ISS ───────────────────────────────────────────────────────────────
      issRef.current.angle += 0.009;
      const issAngle = issRef.current.angle;
      const issX = cx + Math.cos(issAngle) * issOrbitR;
      const issY = cy + Math.sin(issAngle) * issOrbitR;
      drawSatellite(ctx, issX, issY, issAngle + Math.PI / 2, 1.0);

      // ── Launched satellites ───────────────────────────────────────────────
      const sats = satellitesRef.current;
      for (let i = 0; i < sats.length; i++) {
        const sat = sats[i];
        sat.angle += sat.speed;
        const sx = cx + Math.cos(sat.angle) * sat.orbitR;
        const sy = cy + Math.sin(sat.angle) * sat.orbitR;

        sat.trail.push({ x: sx, y: sy });
        if (sat.trail.length > 32) sat.trail.shift();

        // Orbit path
        ctx.beginPath();
        ctx.arc(cx, cy, sat.orbitR, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(126,184,247,0.07)';
        ctx.lineWidth   = 0.5;
        ctx.stroke();

        // Trail
        if (sat.trail.length > 1) {
          ctx.beginPath();
          ctx.moveTo(sat.trail[0].x, sat.trail[0].y);
          for (let j = 1; j < sat.trail.length; j++) ctx.lineTo(sat.trail[j].x, sat.trail[j].y);
          ctx.strokeStyle = 'rgba(126,184,247,0.3)';
          ctx.lineWidth   = 1;
          ctx.stroke();
        }

        drawSatellite(ctx, sx, sy, sat.angle + Math.PI / 2, 0.85);
      }

      // ── Debris particles ───────────────────────────────────────────────────
      const deb = debrisRef.current;
      for (let i = deb.length - 1; i >= 0; i--) {
        const p = deb[i];
        p.x   += p.vx;
        p.y   += p.vy;
        p.vy  += 0.05;
        p.life -= 0.035;
        if (p.life <= 0) { deb.splice(i, 1); continue; }
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,220,150,${p.life.toFixed(2)})`;
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    }

    rafRef.current = requestAnimationFrame(draw);

    function handleClick() {
      if (loadProgressRef.current >= 100) return;
      launchSatellite(canvas);
    }
    canvas.addEventListener('click', handleClick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      canvas.removeEventListener('click', handleClick);
      window.removeEventListener('resize', resize);
      satellitesRef.current.length = 0;
      debrisRef.current.length     = 0;
      if (!calledRef.current && onComplete) { calledRef.current = true; onComplete(); }
    };
  }, [launchSatellite, onComplete]);

  // ── Fire onComplete when progress hits 100 ────────────────────────────────────
  useEffect(() => {
    if (loadProgress < 100) return;
    const elapsed = Date.now() - startTimeRef.current;
    const wait    = Math.max(0, minDisplayMs - elapsed);
    const timer   = setTimeout(() => {
      if (!calledRef.current) { calledRef.current = true; onComplete?.(); }
    }, wait);
    return () => clearTimeout(timer);
  }, [loadProgress, onComplete, minDisplayMs]);

  return { canvasRef, score, loadProgress };
}

// ── Satellite shape helper ────────────────────────────────────────────────────
function drawSatellite(ctx, x, y, angle, opacity) {
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.translate(x, y);
  ctx.rotate(angle);
  // Body
  ctx.fillStyle = '#c0d4e8';
  ctx.fillRect(-7, -3, 14, 6);
  // Solar panels
  ctx.fillStyle = '#5a9ee0';
  ctx.fillRect(-16, -2, 7, 4);
  ctx.fillRect(9,   -2, 7, 4);
  // Antenna
  ctx.strokeStyle = '#8ab8f0';
  ctx.lineWidth   = 0.8;
  ctx.beginPath();
  ctx.moveTo(0, -3);
  ctx.lineTo(0, -7);
  ctx.stroke();
  ctx.restore();
}
